import { useState, useEffect, useMemo, ReactNode, useCallback } from 'react';
import { useAuth0, User } from '@auth0/auth0-react';
import { AuthenticationError, AuthMethod, KeyAuthData, SessionStatus } from '../types/AuthTypes';
import { useLogger } from './useLogger';
import { AuthAppContext } from './useAuthAppContext';
import { ENV } from '../config/env';

const AUTH_STORAGE_KEY = 'key_auth_data';
const AuthenticationRequiredMessage = 'Authentication required';

interface AuthAppProviderProps {
  children: ReactNode;
}

export const AuthAppProvider: React.FC<AuthAppProviderProps> = ({ children }) => {
  const logger = useLogger();
  const {
    isAuthenticated: isAuth0Authenticated,
    isLoading: isAuth0Loading,
    getAccessTokenSilently,
  } = useAuth0();
  const [keyToken, setKeyToken] = useState<string | null>(null);
  const [keyUser, setKeyUser] = useState<User | null>(null);
  const [sessionStatus, setSessionStatus] = useState<SessionStatus>('checking');

  // Load key token from storage on mount
  useEffect(() => {
    try {
      const storedData = sessionStorage.getItem(AUTH_STORAGE_KEY);
      if (storedData) {
        const authData: KeyAuthData = JSON.parse(storedData);

        // Optional: Check token expiration here if VITE_KEY_TOKEN_EXPIRATION is set
        const tokenExpiration = ENV.KEY_TOKEN_EXPIRATION;
        if (tokenExpiration) {
          const expirationMs = Number.parseInt(tokenExpiration, 10) * 1000;
          const isExpired = Date.now() - authData.timestamp > expirationMs;

          if (isExpired) {
            logger.warn('Stored key token has expired, clearing storage');
            sessionStorage.removeItem(AUTH_STORAGE_KEY);
            return;
          }
        }

        setKeyToken(authData.token);
        setKeyUser(authData.user);
        logger.debug('Key token and user loaded from storage');
      }
    } catch (error) {
      logger.error('Failed to load key token from storage:', error);
      sessionStorage.removeItem(AUTH_STORAGE_KEY);
    }
  }, [logger]);

  const authMethod: AuthMethod = useMemo(() => {
    if (isAuth0Authenticated) return 'auth0';
    if (keyToken) return 'key';
    return null;
  }, [isAuth0Authenticated, keyToken]);

  const authenticateWithKey = useCallback(
    (token: string, user: User) => {
      logger.info('🔑 Authenticating with key token');

      const authData: KeyAuthData = {
        token,
        user,
        timestamp: Date.now(),
      };

      sessionStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(authData));
      setKeyToken(token);
      setKeyUser(user);

      logger.debug('Key token and user stored successfully');
    },
    [logger],
  );

  const getAuthToken = useCallback(async (): Promise<string> => {
    if (authMethod === 'auth0') {
      try {
        logger.debug('Getting Auth0 token');
        const token = await getAccessTokenSilently();
        if (!token) {
          throw new AuthenticationError(AuthenticationRequiredMessage);
        }
        return token;
      } catch (error) {
        logger.error('Failed to get Auth0 token:', error);
        throw new AuthenticationError(AuthenticationRequiredMessage);
      }
    }

    if (authMethod === 'key' && keyToken) {
      logger.debug('Returning key token');
      return keyToken;
    }

    logger.error('No valid authentication method available');
    throw new AuthenticationError(AuthenticationRequiredMessage);
  }, [authMethod, keyToken, getAccessTokenSilently, logger]);

  // Vérifie une seule fois, dès qu'une méthode d'auth est connue, que la session est
  // réellement utilisable (et pas seulement présente en cache). Permet aux routes privées
  // de rediriger proprement vers /login avant de tenter un appel API, plutôt que de laisser
  // échouer une requête métier avec une AuthenticationError.
  useEffect(() => {
    if (isAuth0Loading) return;

    if (authMethod === null) {
      setSessionStatus('invalid');
      return;
    }

    if (authMethod === 'key') {
      setSessionStatus('valid');
      return;
    }

    let cancelled = false;
    setSessionStatus('checking');

    getAuthToken()
      .then(() => {
        if (!cancelled) setSessionStatus('valid');
      })
      .catch(() => {
        if (!cancelled) setSessionStatus('invalid');
      });

    return () => {
      cancelled = true;
    };
  }, [isAuth0Loading, authMethod, getAuthToken]);

  const clearKeyAuth = useCallback(() => {
    logger.info('Clearing key authentication');
    sessionStorage.removeItem(AUTH_STORAGE_KEY);
    setKeyToken(null);
    setKeyUser(null);
  }, [logger]);

  const contextValue = useMemo(
    () => ({
      authMethod,
      keyToken,
      keyUser,
      isAuthenticated: isAuth0Authenticated || keyToken !== null,
      sessionStatus,
      authenticateWithKey,
      getAuthToken,
      clearKeyAuth,
      isKeyAuthenticated: authMethod === 'key',
    }),
    [
      authMethod,
      keyToken,
      keyUser,
      isAuth0Authenticated,
      sessionStatus,
      authenticateWithKey,
      getAuthToken,
      clearKeyAuth,
    ],
  );

  return <AuthAppContext.Provider value={contextValue}>{children}</AuthAppContext.Provider>;
};
